import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  getArticoli, createArticolo, updateArticolo, deleteArticolo,
  getFornitori, getArticoliDoppioni, deleteArticoliDoppioni,
  getCategorieArticoli, createCategoriaArticolo, updateCategoriaArticolo, deleteCategoriaArticolo,
  autoClassificaArticoli
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Package, AlertTriangle, CheckCircle, Copy, Tag, Sparkles } from 'lucide-react';

const SENZA_CATEGORIA = '__senza_categoria__';

const ArticoloForm = ({ articolo, fornitori, categorie, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    nome: articolo?.nome || '',
    descrizione: articolo?.descrizione || '',
    prezzo_acquisto: articolo?.prezzo_acquisto || '',
    unita_misura: articolo?.unita_misura || 'pz',
    fornitore_id: articolo?.fornitore_id || '',
    categoria: articolo?.categoria || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome) {
      toast.error('Nome è obbligatorio');
      return;
    }
    setLoading(true);
    try {
      await onSubmit({
        ...formData,
        prezzo_acquisto: formData.prezzo_acquisto ? parseFloat(formData.prezzo_acquisto) : 0,
        categoria: formData.categoria || null
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="nome">Nome Articolo *</Label>
          <Input
            id="nome"
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            placeholder="Nome articolo"
            data-testid="articolo-nome"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="categoria">Categoria</Label>
          <Select
            value={formData.categoria || SENZA_CATEGORIA}
            onValueChange={(value) => setFormData({ ...formData, categoria: value === SENZA_CATEGORIA ? '' : value })}
          >
            <SelectTrigger data-testid="articolo-categoria">
              <SelectValue placeholder="Seleziona categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SENZA_CATEGORIA}>Senza categoria</SelectItem>
              {categorie.map((c) => (
                <SelectItem key={c.id} value={c.nome}>
                  {c.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="fornitore">Fornitore</Label>
          <Select
            value={formData.fornitore_id || SENZA_CATEGORIA}
            onValueChange={(value) => setFormData({ ...formData, fornitore_id: value === SENZA_CATEGORIA ? '' : value })}
          >
            <SelectTrigger data-testid="articolo-fornitore">
              <SelectValue placeholder="Seleziona fornitore" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SENZA_CATEGORIA}>Senza fornitore</SelectItem>
              {fornitori.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="prezzo">Prezzo Acquisto</Label>
          <Input
            id="prezzo"
            type="number"
            step="0.01"
            min="0"
            value={formData.prezzo_acquisto}
            onChange={(e) => setFormData({ ...formData, prezzo_acquisto: e.target.value })}
            placeholder="0.00"
            data-testid="articolo-prezzo"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="unita">Unità di Misura</Label>
          <Select
            value={formData.unita_misura}
            onValueChange={(value) => setFormData({ ...formData, unita_misura: value })}
          >
            <SelectTrigger data-testid="articolo-unita">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pz">Pezzo (pz)</SelectItem>
              <SelectItem value="kg">Chilogrammo (kg)</SelectItem>
              <SelectItem value="lt">Litro (lt)</SelectItem>
              <SelectItem value="cassa">Cassa</SelectItem>
              <SelectItem value="ct">Cartone (ct)</SelectItem>
              <SelectItem value="bt">Bottiglia (bt)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="descrizione">Descrizione</Label>
        <Textarea
          id="descrizione"
          value={formData.descrizione}
          onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
          placeholder="Descrizione articolo..."
          rows={3}
          data-testid="articolo-descrizione"
        />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button type="submit" disabled={loading} data-testid="articolo-submit">
          {loading ? 'Salvataggio...' : articolo ? 'Aggiorna' : 'Crea'}
        </Button>
      </div>
    </form>
  );
};

// ============== GESTIONE CATEGORIE ==============
const CategorieManager = ({ categorie, onChange }) => {
  const [nomeNuovo, setNomeNuovo] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingNome, setEditingNome] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handleAutoClassifica = async () => {
    if (!window.confirm(
      'Analizzerò gli articoli SENZA categoria e assegnerò una categoria automaticamente ' +
      'in base al nome (es. "Coca Cola" → Acqua e Bibite). Le categorie mancanti verranno create.\n\n' +
      'Continuare?'
    )) return;
    setAutoLoading(true);
    setLastResult(null);
    try {
      const res = await autoClassificaArticoli(false);
      setLastResult(res);
      toast.success(
        `${res.articoli_aggiornati} articoli classificati` +
        (res.articoli_non_classificati.length > 0 ? ` (${res.articoli_non_classificati.length} non riconosciuti)` : '')
      );
      onChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore auto-classifica');
    } finally {
      setAutoLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!nomeNuovo.trim()) return;
    setLoading(true);
    try {
      await createCategoriaArticolo({ nome: nomeNuovo.trim() });
      toast.success('Categoria creata');
      setNomeNuovo('');
      onChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditingNome(cat.nome);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingNome('');
  };

  const handleUpdate = async (id) => {
    if (!editingNome.trim()) return;
    try {
      await updateCategoriaArticolo(id, { nome: editingNome.trim() });
      toast.success('Categoria aggiornata');
      cancelEdit();
      onChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento');
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Eliminare la categoria "${cat.nome}"? Gli articoli associati resteranno senza categoria.`)) return;
    try {
      await deleteCategoriaArticolo(cat.id);
      toast.success('Categoria eliminata');
      onChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    }
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Gestione Categorie
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Crea, modifica o elimina le categorie usate per organizzare gli articoli.
          </p>
        </div>

        <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 space-y-2">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium">Auto-classifica articoli senza categoria</p>
              <p className="text-xs text-muted-foreground mt-1">
                Assegna automaticamente una categoria agli articoli che non ne hanno una,
                basandosi sul nome (es. "Coca Cola" → Acqua e Bibite, "Prosciutto crudo" → Salumi e Formaggi).
              </p>
              <Button
                onClick={handleAutoClassifica}
                disabled={autoLoading}
                className="mt-3"
                size="sm"
                data-testid="auto-classifica-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {autoLoading ? 'Classificazione in corso...' : 'Avvia auto-classifica'}
              </Button>
            </div>
          </div>
          {lastResult && (
            <div className="mt-3 pt-3 border-t border-primary/20 text-sm space-y-1">
              <p><strong>{lastResult.articoli_aggiornati}</strong> articoli classificati su <strong>{lastResult.articoli_totali}</strong> totali.</p>
              {lastResult.categorie_create.length > 0 && (
                <p className="text-xs">
                  Categorie create: <strong>{lastResult.categorie_create.join(', ')}</strong>
                </p>
              )}
              {lastResult.articoli_non_classificati.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-amber-600">
                    {lastResult.articoli_non_classificati.length} articoli non riconosciuti (assegnali manualmente)
                  </summary>
                  <ul className="mt-2 ml-4 list-disc space-y-0.5">
                    {lastResult.articoli_non_classificati.slice(0, 20).map((n) => (
                      <li key={n}>{n}</li>
                    ))}
                    {lastResult.articoli_non_classificati.length > 20 && (
                      <li className="italic">... e altri {lastResult.articoli_non_classificati.length - 20}</li>
                    )}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <form onSubmit={handleCreate} className="flex gap-2">
          <Input
            placeholder="Nome nuova categoria (es. Frutta e Verdura)"
            value={nomeNuovo}
            onChange={(e) => setNomeNuovo(e.target.value)}
            data-testid="nuova-categoria-input"
          />
          <Button type="submit" disabled={loading || !nomeNuovo.trim()} data-testid="aggiungi-categoria-btn">
            <Plus className="h-4 w-4 mr-2" />
            Aggiungi
          </Button>
        </form>

        {categorie.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Tag className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>Nessuna categoria. Aggiungi la prima per iniziare.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {categorie.map((cat) => (
              <div
                key={cat.id}
                className="flex items-center justify-between p-3 border rounded-lg bg-secondary/20"
                data-testid={`categoria-row-${cat.id}`}
              >
                {editingId === cat.id ? (
                  <>
                    <Input
                      autoFocus
                      value={editingNome}
                      onChange={(e) => setEditingNome(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdate(cat.id);
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      className="flex-1 mr-2"
                    />
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => handleUpdate(cat.id)}>Salva</Button>
                      <Button size="sm" variant="outline" onClick={cancelEdit}>Annulla</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="font-medium">{cat.nome}</span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => startEdit(cat)} data-testid={`edit-cat-${cat.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(cat)}
                        data-testid={`delete-cat-${cat.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Articoli = () => {
  const [articoli, setArticoli] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArticolo, setEditingArticolo] = useState(null);
  const [filterFornitore, setFilterFornitore] = useState('all');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [showDoppioni, setShowDoppioni] = useState(false);
  const [doppioni, setDoppioni] = useState(null);
  const [loadingDoppioni, setLoadingDoppioni] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [articoliData, fornitoriData, categorieData] = await Promise.all([
        getArticoli(),
        getFornitori(),
        getCategorieArticoli()
      ]);
      setArticoli(articoliData);
      setFornitori(fornitoriData);
      setCategorie(categorieData);
    } catch (error) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const reloadCategorie = async () => {
    try {
      const [categorieData, articoliData] = await Promise.all([getCategorieArticoli(), getArticoli()]);
      setCategorie(categorieData);
      setArticoli(articoliData);
    } catch (e) { /* ignore */ }
  };

  const checkDoppioni = async () => {
    setLoadingDoppioni(true);
    try {
      const data = await getArticoliDoppioni();
      setDoppioni(data);
      setShowDoppioni(true);
      if (data.totale_doppioni === 0) {
        toast.success('Nessun doppione trovato!');
      } else {
        toast.warning(`Trovati ${data.articoli_duplicati} articoli duplicati in ${data.totale_doppioni} gruppi`);
      }
    } catch (error) {
      toast.error('Errore nel controllo doppioni');
    } finally {
      setLoadingDoppioni(false);
    }
  };

  const handleDeleteDoppioni = async (nome, keepId) => {
    if (!window.confirm(`Eliminare tutti i doppioni di "${nome}" tranne uno?`)) return;
    try {
      const result = await deleteArticoliDoppioni(nome, keepId);
      toast.success(result.message);
      await checkDoppioni();
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleCreate = async (data) => {
    try {
      await createArticolo(data);
      toast.success('Articolo creato!');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleUpdate = async (data) => {
    try {
      await updateArticolo(editingArticolo.id, data);
      toast.success('Articolo aggiornato!');
      setDialogOpen(false);
      setEditingArticolo(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo articolo?')) return;
    try {
      await deleteArticolo(id);
      toast.success('Articolo eliminato!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const openEdit = (articolo) => {
    setEditingArticolo(articolo);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingArticolo(null);
  };

  const filteredArticoli = articoli.filter((a) => {
    if (filterFornitore !== 'all' && a.fornitore_id !== filterFornitore) return false;
    if (filterCategoria !== 'all') {
      if (filterCategoria === SENZA_CATEGORIA) {
        if (a.categoria) return false;
      } else if (a.categoria !== filterCategoria) {
        return false;
      }
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const renderArticoliTab = () => (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-muted-foreground">
          {isAdmin ? 'Gestisci i tuoi articoli' : 'Visualizza articoli (sola lettura)'}
        </p>
        <div className="flex gap-2 flex-wrap">
          <Select value={filterCategoria} onValueChange={setFilterCategoria}>
            <SelectTrigger className="w-[180px]" data-testid="filter-categoria">
              <SelectValue placeholder="Filtra per categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le categorie</SelectItem>
              <SelectItem value={SENZA_CATEGORIA}>Senza categoria</SelectItem>
              {categorie.map((c) => (
                <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterFornitore} onValueChange={setFilterFornitore}>
            <SelectTrigger className="w-[180px]" data-testid="filter-fornitore">
              <SelectValue placeholder="Filtra per fornitore" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i fornitori</SelectItem>
              {fornitori.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Button variant="outline" onClick={checkDoppioni} disabled={loadingDoppioni} data-testid="check-doppioni-btn">
              <Copy className="h-4 w-4 mr-2" />
              {loadingDoppioni ? 'Controllo...' : 'Controlla Doppioni'}
            </Button>
          )}
          {isAdmin && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setEditingArticolo(null)} data-testid="add-articolo-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuovo Articolo
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                  <DialogTitle>{editingArticolo ? 'Modifica Articolo' : 'Nuovo Articolo'}</DialogTitle>
                  <DialogDescription>
                    {editingArticolo ? 'Modifica i dati dell\'articolo' : 'Inserisci i dati del nuovo articolo'}
                  </DialogDescription>
                </DialogHeader>
                <ArticoloForm
                  articolo={editingArticolo}
                  fornitori={fornitori}
                  categorie={categorie}
                  onSubmit={editingArticolo ? handleUpdate : handleCreate}
                  onCancel={closeDialog}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {showDoppioni && doppioni && (
        <Card className="border-amber-500/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {doppioni.totale_doppioni > 0 ? (
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                )}
                <h3 className="font-semibold">
                  {doppioni.totale_doppioni > 0
                    ? `${doppioni.articoli_duplicati} articoli duplicati in ${doppioni.totale_doppioni} gruppi`
                    : 'Nessun doppione trovato!'}
                </h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowDoppioni(false)}>Chiudi</Button>
            </div>
            {doppioni.gruppi.length > 0 && (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {doppioni.gruppi.map((gruppo, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-secondary/30">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="destructive">{gruppo.count}x</Badge>
                        <span className="font-medium">{gruppo.nome}</span>
                      </div>
                      <Button size="sm" variant="outline" className="text-destructive hover:text-destructive"
                        onClick={() => handleDeleteDoppioni(gruppo.nome, gruppo.articoli[0]?.id)}>
                        <Trash2 className="h-4 w-4 mr-1" />
                        Elimina Doppioni
                      </Button>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      {gruppo.articoli.map((art, i) => (
                        <div key={art.id} className="flex items-center gap-2">
                          {i === 0 && <Badge variant="outline" className="text-xs">Mantieni</Badge>}
                          <span className="font-mono text-xs">{art.id.slice(0, 8)}...</span>
                          <span>{art.fornitore_id ? `Fornitore: ${art.fornitore_id.slice(0, 8)}...` : 'Senza fornitore'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {filteredArticoli.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nessun articolo trovato</p>
            <p className="text-sm text-muted-foreground">
              {isAdmin ? 'Clicca su "Nuovo Articolo" per iniziare' : 'Nessun risultato con i filtri attuali'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Fornitore</TableHead>
                    <TableHead>Prezzo</TableHead>
                    <TableHead className="hidden md:table-cell">Unità</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredArticoli.map((articolo) => (
                    <TableRow key={articolo.id} data-testid={`articolo-row-${articolo.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{articolo.nome}</p>
                          {articolo.descrizione && (
                            <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                              {articolo.descrizione}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {articolo.categoria ? (
                          <Badge variant="secondary" className="font-normal">{articolo.categoria}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{articolo.fornitore_nome || '—'}</TableCell>
                      <TableCell className="font-mono font-medium">
                        {articolo.prezzo_acquisto ? `€${articolo.prezzo_acquisto.toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">{articolo.unita_misura}</TableCell>
                      <TableCell className="text-right">
                        {isAdmin ? (
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(articolo)} data-testid={`edit-articolo-${articolo.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive"
                              onClick={() => handleDelete(articolo.id)} data-testid={`delete-articolo-${articolo.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Sola lettura</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="articoli-page">
      <div>
        <h1 className="text-3xl font-bold">Articoli</h1>
      </div>

      {isAdmin ? (
        <Tabs defaultValue="articoli" className="space-y-4">
          <TabsList>
            <TabsTrigger value="articoli" data-testid="tab-articoli">
              <Package className="h-4 w-4 mr-2" />
              Articoli ({articoli.length})
            </TabsTrigger>
            <TabsTrigger value="categorie" data-testid="tab-categorie">
              <Tag className="h-4 w-4 mr-2" />
              Categorie ({categorie.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="articoli" className="space-y-4">
            {renderArticoliTab()}
          </TabsContent>
          <TabsContent value="categorie">
            <CategorieManager categorie={categorie} onChange={reloadCategorie} />
          </TabsContent>
        </Tabs>
      ) : (
        <div className="space-y-4">{renderArticoliTab()}</div>
      )}
    </div>
  );
};

export default Articoli;
